import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { debounce, uniqBy } from 'lodash-es';
import { affinityToStem } from '@ourbranch/policy-utils';

import { useStore } from 'core/store';
import { TextField } from 'core/components/text-field';
import { Autocomplete } from 'core/components/autocomplete';
import useStyles from './agency-reports.styles';

// Same debounce as the quote form's affinity field (form/field-types/affinity.jsx).
const SEARCH_DEBOUNCE_MS = 600;

/** Mirrors the lambda's normalisation: trim, upper-case, reduce the code to its stem. */
const toStem = (value) => {
  const token = (value ?? '').trim().toUpperCase();
  if (!/^[A-Z0-9]+(?:_[A-Z0-9]+)?$/.test(token)) {
    return null;
  }
  return affinityToStem(token) || null;
};

/**
 * Agency picker for the internal Agency Reports page (#36219): the affinity-lookups search every
 * other staff affinity field uses (name / code / agent, fuzzy), but what it hands back is the agency
 * STEM — reports are filed per stem, so picking BIG_123 or BIGP both resolve to BIG. A stem typed
 * straight in (Enter) is accepted too, since not every stem has a lookup row under that exact code.
 */
const AgencyStemSearch = ({ onSelect }) => {
  const classes = useStyles();
  const { affinityLookups } = useStore();
  const abortControllerRef = useRef(null);
  const [inputValue, setInputValue] = useState('');

  const debouncedSearch = useMemo(
    () =>
      debounce((value, signal) => {
        affinityLookups.getSuggestedAffinities({
          queryParams: { affinity: value, name: value },
          requestOptions: { signal },
          // Reports are historical: a disabled agency still has months to inspect, so (unlike the
          // quote form's field) disabled lookups stay in.
          excludeDisabled: false,
          limitedList: false
        });
      }, SEARCH_DEBOUNCE_MS),
    [affinityLookups]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const search = (value) => {
    // Cancel the in-flight lookup before firing the next one, as the quote form's field does.
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    debouncedSearch(value, abortController.signal);
  };

  // Same label as the quote form's affinity options, but WITHOUT its hideFromOffers filter: that is a
  // new-offer rule, and an agency hidden from quoting still has reports internal staff need to find.
  const options = uniqBy(
    affinityLookups.data.map((partner) => {
      const producerName = [partner.producerFirstName, partner.producerLastName].filter(Boolean).join(' ');
      return { id: partner.affinity, text: [partner.name, partner.affinity, producerName].filter(Boolean).join(' - ') };
    }),
    'id'
  );

  const select = (code) => {
    const stem = toStem(code);
    if (stem) {
      onSelect(stem);
    }
  };

  return (
    <Autocomplete
      id="agency-reports-agency"
      name="agency"
      className={classes.search}
      // The lookups store is shared (the quote form fills it too): offer nothing until the user has
      // typed, so a stale result set is not presented on focus.
      options={inputValue.trim() ? options : []}
      // The lookups endpoint already fuzzy-matched these; MUI's default substring filter would
      // throw away any hit whose label doesn't literally contain the input.
      filterOptions={(serverMatches) => serverMatches}
      value={null}
      inputValue={inputValue}
      loading={affinityLookups.loading || affinityLookups.partialLoading}
      InputComponent={TextField}
      onInputChange={(_, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === 'input') {
          search(newInputValue);
        }
      }}
      // An option from the lookups, or (freeSolo) the raw text on Enter.
      onSelection={(_, selected) => select(typeof selected === 'string' ? selected : selected?.id)}
      inputProps={{ label: 'Agency', mode: 'light', placeholder: 'Agency name, affinity code or stem' }}
    />
  );
};

AgencyStemSearch.propTypes = {
  onSelect: PropTypes.func.isRequired
};

export default observer(AgencyStemSearch);
